import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import type { UserRole } from '@study-decks/shared'

import { useAuth } from './AuthContext.js'

const ROLE_HOME: Record<UserRole, string> = {
  student: '/student',
  teacher: '/teacher',
  admin: '/admin',
  pending: '/pending',
}

export interface AuthGateProps {
  allow: UserRole[]
  children: ReactNode
}

export function AuthGate({ allow, children }: AuthGateProps): JSX.Element {
  const { status, role } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return (
      <main id="main-content">
        <p role="status" aria-live="polite">
          Checking your session…
        </p>
      </main>
    )
  }

  if (status === 'unauthenticated') {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to={`/?returnTo=${encodeURIComponent(returnTo)}`} replace />
  }

  if (!role || !allow.includes(role)) {
    const target = role ? ROLE_HOME[role] : '/'
    return <Navigate to={target} replace />
  }

  return <>{children}</>
}
